import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserDetailResponse } from '../../api/generated';
import { Checkbox } from '../UI';
import {
  LOCATION_ROLE_DESCRIPTION_KEYS,
  LOCATION_ROLE_LABEL_KEYS,
  LOCATION_ROLE_ORDER,
  LOCATION_ROLE_SHORT_LABEL_KEYS,
  type LocationRole
} from '../../constants/locationRoles';
import type { LocationUserRoles } from '../../services/locationUserService';
import { getUserDisplayName } from '../../services/userLabelService';
import { buildRoleCheckboxId } from './locationsConstants';
import styles from './LocationUserRolesGrid.module.css';

interface LocationUserRolesGridProps {
  users: UserDetailResponse[];
  assignments: LocationUserRoles[];
  onToggle: (userId: string, role: LocationRole) => void;
  // The signed-in administrator, whose own last role is locked. Access to a
  // location comes from holding at least one role there and being a global
  // administrator is not a bypass, so clearing the last one would hide the
  // location from the very person editing it. Null when signed out, which
  // matches no row.
  lockedUserId?: string | null;
}

// The same roles table as the Users page, transposed: one row per user, one
// column per role.
export const LocationUserRolesGrid = ({
  users,
  assignments,
  onToggle,
  lockedUserId
}: LocationUserRolesGridProps) => {
  const { t } = useTranslation();

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b))),
    [users]
  );

  return (
    <div className={styles.grid}>
      <div className={`${styles.gridRow} ${styles.gridHeader}`}>
        <span className={styles.gridUser}>{t('config-loc-col-user')}</span>
        {LOCATION_ROLE_ORDER.map((role) => (
          <span
            key={role}
            className={styles.gridRole}
            title={`${t(LOCATION_ROLE_LABEL_KEYS[role])} — ${t(LOCATION_ROLE_DESCRIPTION_KEYS[role])}`}
          >
            {t(LOCATION_ROLE_SHORT_LABEL_KEYS[role])}
          </span>
        ))}
      </div>

      {sortedUsers.map((user) => {
        // A user with no entry yet (the list resolved after the form was seeded)
        // shows as holding no role rather than vanishing from the grid.
        const held = assignments.find((entry) => entry.userId === user.userId)?.roles ?? [];
        return (
          <div key={user.userId} className={styles.gridRow}>
            <span className={styles.gridUser}>
              <span className={styles.userName}>{getUserDisplayName(user)}</span>
              <span className={styles.userEmail}>{user.email}</span>
            </span>
            {LOCATION_ROLE_ORDER.map((role) => {
              const isHeld = held.includes(role);
              // Locked only when it is the LAST role held: any other tick can be
              // cleared without losing the location, and disabling those would
              // read as "you may not change your own roles", which is not the
              // rule.
              const isLocked = user.userId === lockedUserId && isHeld && held.length === 1;
              return (
                <span
                  key={role}
                  className={styles.gridRole}
                  // Explains the one box that cannot be unticked. On the span
                  // rather than the input, because a disabled control is not
                  // hoverable in every browser.
                  title={isLocked ? t('config-loc-role-self-last-locked') : undefined}
                >
                  <Checkbox
                    id={buildRoleCheckboxId(user.userId, role)}
                    isChecked={isHeld}
                    onChange={() => onToggle(user.userId, role)}
                    isDisabled={isLocked}
                    // Hidden, since the column header carries the visible text,
                    // but it still names the box for screen readers. The locked
                    // box says why, so it does not just read as unavailable.
                    label={
                      isLocked
                        ? t('config-loc-role-self-last-locked')
                        : t('config-loc-role-aria', {
                            role: t(LOCATION_ROLE_LABEL_KEYS[role]),
                            user: getUserDisplayName(user)
                          })
                    }
                    isLabelHidden
                  />
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
